import { useEffect, useState, type RefObject } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, Input, MessageBar, MessageBarBody, Select, Text, makeStyles, tokens } from '@fluentui/react-components';
import { useT } from '../i18n';
import { attendanceErrorMessage } from '../utils/attendance-error';
import { fetchAccessConfig, identifyAtGate, registerPersonPass, type AccessDirection, type GateFace as Face } from '../api/access.api';

const useStyles = makeStyles({
  row: { display: 'flex', gap: tokens.spacingHorizontalS, flexWrap: 'wrap', alignItems: 'center' },
  video: { width: '100%', maxWidth: '420px', borderRadius: tokens.borderRadiusMedium },
  card: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS, padding: tokens.spacingVerticalS, border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusMedium },
  muted: { color: tokens.colorNeutralForeground3 },
});

const MAX_W = 1024;

/** Caseta con cámara: foto → Rekognition → el vigilante confirma y se registra el paso. No guarda la foto. */
export function GateFace({ onLogged, video, active }: { onLogged: (text: string, ok: boolean) => void; video: RefObject<HTMLVideoElement>; active: boolean }) {
  const t = useT();
  const g = t.gatePage;
  const s = useStyles();
  const [direction, setDirection] = useState<AccessDirection>('IN');
  const [faces, setFaces] = useState<Face[] | null>(null);
  const [visitor, setVisitor] = useState('');
  const [reason, setReason] = useState('');
  const cfg = useQuery({ queryKey: ['access-config'], queryFn: fetchAccessConfig, retry: false });
  const policy = cfg.data?.terminatedPolicies?.[direction] ?? 'STRICT';
  const canAuthorize = !!cfg.data?.canAuthorize;

  useEffect(() => { if (!active) setFaces(null); }, [active]);

  const identify = useMutation({
    mutationFn: async () => {
      const el = video.current;
      if (!el || !el.videoWidth) throw new Error('camera');
      const c = document.createElement('canvas');
      const w = Math.min(MAX_W, el.videoWidth);
      c.width = w; c.height = Math.round((el.videoHeight * w) / el.videoWidth);
      c.getContext('2d')!.drawImage(el, 0, 0, c.width, c.height);
      return identifyAtGate(c.toDataURL('image/jpeg', 0.85).split(',')[1]);
    },
    onSuccess: (r) => { setFaces(r); setVisitor(''); },
    onError: (e) => onLogged(attendanceErrorMessage(e, t.attendancePage.errors, g.faceError), false),
  });

  const pass = useMutation({
    mutationFn: (b: Parameters<typeof registerPersonPass>[0]) => registerPersonPass(b),
    onSuccess: (r) => {
      const text = r.result === 'ALLOWED' || r.result === 'AUTHORIZED' ? g.personLogged(r.name)
        : r.result === 'VISITOR' ? g.visitorLogged(r.name)
        : r.result === 'PENDING' ? g.terminatedPending(r.name) : g.terminatedDenied(r.name);
      onLogged(text, r.result === 'ALLOWED' || r.result === 'AUTHORIZED' || r.result === 'VISITOR');
      setFaces(null); setReason('');
    },
    onError: (e) => onLogged(attendanceErrorMessage(e, t.attendancePage.errors, t.attendancePage.genericError), false),
  });

  return (
    <div className={s.card}>
      <div className={s.row}>
        {!active && <Text className={s.muted}>{g.openCameraFirst}</Text>}
        {active && (
          <>
            <Select value={direction} onChange={(_, d) => setDirection(d.value as AccessDirection)}>
              <option value="IN">{g.entry}</option><option value="OUT">{g.exit}</option>
            </Select>
            <Button appearance="primary" disabled={identify.isPending} onClick={() => identify.mutate()}>{g.identify}</Button>
          </>
        )}
      </div>
      {faces?.map((f) => (
        <div key={f.index} className={s.card}>
          {f.kind === 'AUTO' && (
            <>
              <Text weight="semibold">{f.employee.name} · {f.similarity.toFixed(1)}%</Text>
              <Button appearance="primary" disabled={pass.isPending}
                onClick={() => pass.mutate({ direction, source: 'FACE', employeeId: f.employee.employeeId, method: 'AUTO', similarity: f.similarity })}>
                {direction === 'IN' ? g.letIn : g.letOut}
              </Button>
            </>
          )}
          {f.kind === 'TERMINATED' && (
            <>
              <MessageBar intent="error"><MessageBarBody>{g.terminated.replace('{name}', f.employee.name)}</MessageBarBody></MessageBar>
              <div className={s.row}>
                {policy === 'AUTHORIZE' && canAuthorize && (
                  <Input placeholder={g.authReason} value={reason} onChange={(_, d) => setReason(d.value)} />
                )}
                <Button disabled={pass.isPending || (policy === 'AUTHORIZE' && canAuthorize && reason.trim().length < 3)}
                  onClick={() => pass.mutate({ direction, source: 'FACE', employeeId: f.employee.employeeId, method: 'CONFIRMED', similarity: f.similarity, ...(reason.trim() ? { reason: reason.trim() } : {}) })}>
                  {policy === 'VISITOR' ? g.logAsVisitor : policy === 'AUTHORIZE' ? (canAuthorize ? g.authorizePass : g.requestAuth) : g.logDenied}
                </Button>
              </div>
            </>
          )}
          {f.kind === 'NEEDS_CONFIRMATION' && (
            <>
              <Text>{g.pickCandidate}</Text>
              <div className={s.row}>
                {f.candidates.map((c) => c.status === 'INACTIVE' ? (
                  <MessageBar key={c.employeeId} intent="error"><MessageBarBody>{g.terminated.replace('{name}', c.name)}</MessageBarBody></MessageBar>
                ) : (
                  <Button key={c.employeeId} disabled={pass.isPending}
                    onClick={() => pass.mutate({ direction, source: 'FACE', employeeId: c.employeeId, method: 'CONFIRMED', similarity: c.similarity })}>
                    {c.name} · {c.similarity.toFixed(0)}%
                  </Button>
                ))}
              </div>
            </>
          )}
          {(f.kind === 'UNKNOWN' || f.kind === 'NEEDS_CONFIRMATION') && (
            <div className={s.row}>
              <Input placeholder={g.visitorName} value={visitor} onChange={(_, d) => setVisitor(d.value)} />
              <Button disabled={visitor.trim().length < 3 || pass.isPending}
                onClick={() => pass.mutate({ direction, source: 'FACE', visitorName: visitor.trim() })}>{g.logVisitor}</Button>
            </div>
          )}
          {f.kind === 'TOO_SMALL' && <Text className={s.muted}>{g.tooSmall}</Text>}
        </div>
      ))}
    </div>
  );
}
